import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = {
  therapistId: "",
  client_name: "",
  client_phone: "",
  appointment_datetime: "",
  price: "",
  notes: "",
  payment_status: "pendiente",
  duration: 60,
};

export const AppointmentModal = ({ open, onClose, onSave, appointment, therapists = [], forceNew = false }) => {
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (appointment) {
      setForm({
        therapistId: appointment.therapistId || therapists[0]?.id || "",
        client_name: appointment.client_name || "",
        client_phone: appointment.client_phone || "",
        appointment_datetime: appointment.appointment_datetime
          ? appointment.appointment_datetime.slice(0, 16)
          : "",
        price: appointment.price ?? "",
        notes: appointment.notes || "",
        payment_status: appointment.payment_status || "pendiente",
        duration: appointment.duration || 60,
      });
    } else {
      setForm({ ...empty, therapistId: therapists[0]?.id || "" });
    }
  }, [appointment, open, therapists]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid =
    form.client_name.trim() && form.client_phone.trim() && form.appointment_datetime;

  const handleSave = () => {
    if (!valid) return;
    onSave({
      therapistId: form.therapistId,
      client_name: form.client_name.trim(),
      client_phone: form.client_phone.trim(),
      appointment_datetime: new Date(form.appointment_datetime).toISOString(),
      price: parseFloat(form.price) || 0,
      notes: form.notes,
      payment_status: form.payment_status,
      duration: parseInt(form.duration, 10) || 60,
    });
  };

  const isPaid = form.payment_status === "pagado";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="appointment-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-slate-900">
            {appointment && !forceNew ? "Editar cita" : "Añadir cita"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Rellena los datos de la cita y guárdala.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {therapists.length > 1 && (
            <div className="space-y-1.5">
              <Label>Terapeuta</Label>
              <Select
                value={form.therapistId}
                onValueChange={(v) => setForm((f) => ({ ...f, therapistId: v }))}
              >
                <SelectTrigger data-testid="select-therapist">
                  <SelectValue placeholder="Elige terapeuta" />
                </SelectTrigger>
                <SelectContent>
                  {therapists.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="name">Nombre del cliente</Label>
            <Input id="name" data-testid="input-client-name" value={form.client_name} onChange={set("client_name")} placeholder="Escribe el nombre" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">Teléfono</Label>
            <Input id="phone" data-testid="input-client-phone" value={form.client_phone} onChange={set("client_phone")} placeholder="Para el recordatorio" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="dt">Fecha y hora</Label>
              <Input id="dt" type="datetime-local" data-testid="input-datetime" value={form.appointment_datetime} onChange={set("appointment_datetime")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="price">Precio (€)</Label>
              <Input id="price" type="number" min="0" step="1" data-testid="input-price" value={form.price} onChange={set("price")} placeholder="0" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Duración</Label>
            <div className="flex gap-2">
              {[30, 60, 90].map((min) => (
                <button
                  key={min}
                  type="button"
                  data-testid={`duration-${min}`}
                  onClick={() => setForm((f) => ({ ...f, duration: min }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${Number(form.duration) === min ? "bg-[#275FBE]/10 border-[#275FBE] text-[#275FBE]" : "bg-white border-slate-200 text-slate-400"}`}
                >
                  {min} min
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notas de la sesión</Label>
            <Textarea id="notes" data-testid="input-notes" value={form.notes} onChange={set("notes")} placeholder="Apunta lo que quieras recordar" rows={3} />
          </div>

          <div className="space-y-1.5">
            <Label>Estado del pago</Label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="toggle-pending"
                onClick={() => setForm((f) => ({ ...f, payment_status: "pendiente" }))}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  !isPaid ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-white border-slate-200 text-slate-400"
                }`}
              >
                Pendiente
              </button>
              <button
                type="button"
                data-testid="toggle-paid"
                onClick={() => setForm((f) => ({ ...f, payment_status: "pagado" }))}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  isPaid ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-white border-slate-200 text-slate-400"
                }`}
              >
                Pagado
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1 rounded-full" onClick={onClose} data-testid="cancel-button">
            Cerrar
          </Button>
          <Button
            className="flex-1 rounded-full text-white"
            style={{ backgroundColor: "#275FBE" }}
            disabled={!valid}
            onClick={handleSave}
            data-testid="save-appointment-button"
          >
            Guardar cita
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
